import React, {
  useState,
  ReactNode,
  forwardRef,
  useImperativeHandle,
  ForwardRefRenderFunction,
} from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

export interface Ref {
  show: boolean;
  handleShowModal: (show: boolean) => void;
}

interface Props {
  children: ReactNode;
  titleElement: ReactNode;
  titleClassName?: string;
  iconButtonColor?: string;
  footerClassName?: string;
  actionButtonText?: string;
  handleNewTab?: () => void;
  defaultShowModal?: boolean;
  enableCloseButton?: boolean;
  enableActionButton?: boolean;
  handleCloseAction?: () => void;
  disabledActionButton?: boolean;
  enableCloseHeaderIcon?: boolean;
  mainContainerClassName?: string;
  handleActionButton?: () => void;
  titleComponent?: React.ReactNode;
  enableNewTabHeaderIcon?: boolean;
  width?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

const Modal: ForwardRefRenderFunction<Ref, Props> = (
  {
    children,
    titleElement,
    handleNewTab,
    width = 'md',
    actionButtonText,
    handleCloseAction,
    handleActionButton,
    titleClassName = '',
    footerClassName = '',
    enableCloseButton = true,
    defaultShowModal = false,
    enableActionButton = false,
    mainContainerClassName = '',
    enableCloseHeaderIcon = true,
    disabledActionButton = false,
    enableNewTabHeaderIcon = false,
    iconButtonColor = 'var(--color-contrast)',
  },
  ref
) => {
  const [open, setOpen] = useState(defaultShowModal);

  const handleShowModal = (show: boolean) => setOpen(show);

  useImperativeHandle(ref, () => ({
    handleShowModal,
    show: open,
  }));

  const handleClose = (event?: any, reason?: string) => {
    if (reason && reason == 'backdropClick') {
      return;
    }
    if (handleCloseAction) handleCloseAction();
    handleShowModal(false);
  };

  return (
    <Dialog
      fullWidth
      open={open}
      maxWidth={width}
      disableEscapeKeyDown
      onClose={handleClose}
      className={`modal-container  ${mainContainerClassName}`}
    >
      <DialogTitle className={`modal-title ${titleClassName}`}>
        {titleElement}
      </DialogTitle>
      {enableNewTabHeaderIcon && (
        <Tooltip title={'Open in a new tab'}>
          <IconButton
            className="modal-fullscreen-btn"
            onClick={() => handleNewTab && handleNewTab()}
          >
            <OpenInNewIcon htmlColor={iconButtonColor} />
          </IconButton>
        </Tooltip>
      )}
      {enableCloseHeaderIcon && (
        <Tooltip title={'Close'}>
          <IconButton className="modal-close-btn" onClick={handleClose}>
            <CloseIcon htmlColor={iconButtonColor} />
          </IconButton>
        </Tooltip>
      )}

      <DialogContent dividers>{children}</DialogContent>
      <DialogActions className={`actions-container ${footerClassName}`}>
        {enableActionButton && (
          <Button
            variant="contained"
            className="apply-btn"
            disabled={disabledActionButton}
            onClick={() => handleActionButton && handleActionButton()}
          >
            {actionButtonText}
          </Button>
        )}
        {enableCloseButton && (
          <Button
            variant="contained"
            className="close-btn"
            onClick={handleClose}
          >
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default forwardRef<Ref, Props>(Modal);
